import React, { useCallback, useState } from 'react';
import { Row, Col, Button, InputGroup, Form } from 'react-bootstrap-5';
import { connect } from 'react-redux';

import { type CallList, type CallListItem, type InsightsProperty } from 'bundles/connect/types';
import RefreshOwnersModal from 'bundles/connect/components/off_market_properties/owners/refresh_owners_modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight, faCaretDown, faSearch } from '@fortawesome/pro-solid-svg-icons';
import {
  fetchCallListItems,
  callListUrl,
  CALL_LISTS_INDEX,
  CALL_LISTS_NEXT,
  CALL_LISTS_PREVIOUS,
  CALL_LISTS_SEARCH,
  CALL_LISTS_SHOW
} from 'bundles/connect/helpers/prospect_api';
import { debounce } from 'lodash';
import { rethinkPropertyPath } from 'bundles/connect/helpers/rethink_helper';
import { formatDateString } from '../../helpers/date_helpers';
import ScoreBadges from '../search/components/results/score_badges';


export type CallConsoleHeaderProps = {
  callList: CallList;
  callListLength: number;
  insightsProperty?: InsightsProperty;
  index: number;
  lastFetched: string | null;
  remainingCredits: number;
  rethinkInstanceUrl?: string;
  rethinkPropertyId?: string;
  trialUser: boolean;
};

function CallConsoleHeader({
  callList,
  callListLength,
  insightsProperty,
  index,
  lastFetched,
  remainingCredits,
  rethinkInstanceUrl,
  rethinkPropertyId,
  trialUser
}: CallConsoleHeaderProps) {
  const [callListItems, setCallListItems] = useState<CallListItem[]>([]);
  const [disabled, setDisabled] = useState(false);

  const likelyToSell = insightsProperty?.scores.likelyToSell && insightsProperty.scores.likelyToSell.rank > 0;
  const likelyToRefi = insightsProperty?.scores.likelyToRefi && insightsProperty.scores.likelyToRefi.rank > 0;

  const debouncedSearch = useCallback(
    debounce((query) => {
      search(query);
    }, 300),
    []
  );

  const search = (query: string) => {
    const params = {
      // eslint-disable-next-line max-len
      'q[call_listable_of_Connect::Property_type_address_or_call_listable_of_Connect::Contact_type_first_name_or_call_listable_of_Connect::Contact_type_last_name_or_call_listable_of_Connect::Contact_type_name_cont]': query
    };
    fetchCallListItems(params, callList.id).then((data: { data: CallListItem[] }) => {
      setCallListItems(data.data);
    });
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSearch(event.target.value);
  };

  const handleDropdownClick = () => {
    if (callListItems.length === 0) {
      search('');
    }
  };

  return (
    <>
      <Row className="gy-3">
        <Col className="order-2 order-lg-1" lg>
          <Row className="flex-column gap-2">
            <Col>
              <nav>
                <ol className="breadcrumb my-0 align-items-center">
                  <li className="breadcrumb-item my-0">
                    <a className="text-muted" href={callListUrl(CALL_LISTS_SEARCH)}>
                      <i className="far fa-home"></i>
                    </a>
                  </li>
                  <li className="breadcrumb-item my-0">
                    <a className="text-muted" href={callListUrl(CALL_LISTS_INDEX)}>
                      Lists
                    </a>
                  </li>
                  <li className="breadcrumb-item my-0">
                    <a className="text-muted" href={callListUrl(CALL_LISTS_SHOW, callList.id)}>
                      {callList.name}
                    </a>
                  </li>
                </ol>
              </nav>
            </Col>
            {insightsProperty && (
              <>
                {(likelyToSell || likelyToRefi) && (
                  <Col>
                    <Row className="g-1">
                      <ScoreBadges scores={insightsProperty.scores} />
                    </Row>
                  </Col>
                )}
                <Col>
                  <h5 className="m-0">{insightsProperty.address}</h5>
                </Col>
                <Col>
                  <div className="text-muted m-0">
                    {insightsProperty.city}, {insightsProperty.state} {insightsProperty.zip}
                  </div>
                </Col>
              </>
            )}
          </Row>
        </Col>
        <Col className="order-lg-2" lg="auto">
          <Row className="align-items-center justify-content-center gx-1">
            {lastFetched && (
              <Col xs="auto">
                <RefreshOwnersModal
                  activityConsole={true}
                  insightsPropertyId={insightsProperty?.propertyId}
                  remainingCredits={remainingCredits}
                  trialUser={trialUser}
                />
              </Col>
            )}
            <Col xs="auto">
              <Button
                data-turbo="false"
                disabled={disabled}
                href={callListUrl(CALL_LISTS_PREVIOUS, callList.id, index - 1)}
                variant="text-primary"
                onClick={() => setDisabled(true)}
              >
                <FontAwesomeIcon icon={faArrowLeft} />
                Previous
              </Button>
            </Col>
            <Col className="text-muted" xs="auto">
              <div data-controller="popper" data-popper-animate-in-value="animate__fadeIn">
                <Button
                  data-action="click->popper#toggle"
                  data-popper-target="element"
                  variant="text-primary"
                  onClick={handleDropdownClick}
                >
                  {index + 1} OF {callListLength}
                  <FontAwesomeIcon icon={faCaretDown} />
                </Button>
                <div
                  className="card popover-card d-none"
                  data-popper-target="popper"
                  style={{ maxHeight: '450px', width: '500px' }}
                >
                  <div className="card-header">
                    <Row>
                      <Col xs={6}>
                        <h6>{callList.name}</h6>
                      </Col>
                      <Col className="text-end" xs={6}>
                        <div className="text-muted">{callListLength} Properties</div>
                      </Col>
                      <Col xs={12}>
                        <InputGroup className="flex-nowrap">
                          <InputGroup.Text><FontAwesomeIcon icon={faSearch} /></InputGroup.Text>
                          <Form.Control
                            placeholder="Search for address"
                            onChange={handleChange}
                          />
                        </InputGroup>
                      </Col>
                    </Row>
                  </div>
                  <div className="card-body overflow-auto p-0">
                    <div className="list-group list-group-flush">
                      {callListItems.map((item, index) => (
                        <a className="list-group-item"
                          href={`${callListUrl(CALL_LISTS_SHOW, callList.id)}/call_console?item_id=${item.id}`}
                          key={index}
                        >
                          {item.callListable.name}
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </Col>
            <Col xs="auto">
              <Button
                data-turbo="false"
                disabled={disabled}
                href={callListUrl(CALL_LISTS_NEXT, callList.id, index + 1)}
                variant="text-primary"
                onClick={() => setDisabled(true)}
              >
                Next
                <FontAwesomeIcon icon={faArrowRight} />
              </Button>
            </Col>
            {index >= (callListLength - 1) && (
              <Col xs="auto">
                <Button
                  href={callListUrl(CALL_LISTS_NEXT, callList.id, index + 1)}
                  variant="secondary"
                >
                  Done
                </Button>
              </Col>
            )}
          </Row>
          {rethinkPropertyId && (
            <Row>
              <Col className="text-end">
                <a
                  className="btn btn-link"
                  href={rethinkPropertyPath(rethinkInstanceUrl, rethinkPropertyId)}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  View property in Rethink
                  <i className="fas fa-arrow-up-right-from-square"></i>
                </a>
              </Col>
            </Row>
          )}
          {lastFetched && (
            <Row className="justify-content-end pt-2">
              <Col xs="auto">
                <span className="text-muted m-0">
                  <i className="fa-regular fa-circle-check me-1"></i>
                  Contact Data Fetched on {formatDateString(lastFetched)}
                </span>
              </Col>
            </Row>
          )}
          <div className="d-none d-lg-block d-xl-none text-end" id="js-header-portal-tablet"></div>
        </Col>
        <Col className="d-lg-none order-3" id="js-header-portal-mobile" xs={12}>
        </Col>
      </Row>
    </>
  );
}

const mapStateToProps = ({ rethinkPropertyId }) => ({ rethinkPropertyId });
export default connect(mapStateToProps)(CallConsoleHeader);
